import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Select, Spinner } from "@aigntx/ui";
import { endpoints } from "../api";
import type {
  ArchItem,
  Blueprint,
  CanvasOverview,
  PlatformUsers,
  CanvasProcess,
  CanvasSchema,
  CanvasTable,
  FormType,
  QueryResult,
  Readiness,
  ReadinessCheck,
  ReleaseLog,
  ViewAsTarget,
} from "../types";

type Tab =
  | "map"
  | "release"
  | "arch"
  | "users"
  | "data"
  | "flows"
  | "forms"
  | "query"
  | "viewas";

const TABS: { key: Tab; label: string; hint: string }[] = [
  { key: "map", label: "Overview", hint: "Tenants, admins and live counts" },
  { key: "release", label: "Releases", hint: "Versions and production readiness" },
  { key: "arch", label: "Architecture", hint: "Blueprint — planned vs built" },
  { key: "users", label: "Users", hint: "Tenants and their people" },
  { key: "data", label: "Data model", hint: "Every table and every attribute" },
  { key: "flows", label: "Processes", hint: "How the app actually behaves" },
  { key: "forms", label: "Forms", hint: "Fields, options and routes" },
  { key: "query", label: "Query", hint: "Read-only SQL" },
  { key: "viewas", label: "View as", hint: "Open the app as any user" },
];

/**
 * The platform canvas — one screen that explains the whole system: what it
 * stores, how it behaves, and what is in it right now. Platform admin only;
 * every route it calls is gated server-side too.
 */
export function CanvasPage() {
  const [tab, setTab] = useState<Tab>("map");
  const [overview, setOverview] = useState<CanvasOverview | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    endpoints
      .canvasOverview()
      .then(setOverview)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!overview)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  return (
    <div className="page page--wide">
      <header className="page__head between">
        <div>
          <h1 className="page__title">Platform canvas</h1>
          <p className="page__sub">
            The whole system on one screen — what it stores, how it behaves, and what is
            in it right now. Visible to platform admins only; no tenant can reach it.
          </p>
        </div>
        <Badge tone="danger">Platform admin</Badge>
      </header>

      <nav className="canvas-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={tab === t.key ? "canvas-tab is-on" : "canvas-tab"}
            onClick={() => setTab(t.key)}
          >
            <span className="canvas-tab__label">{t.label}</span>
            <span className="canvas-tab__hint">{t.hint}</span>
          </button>
        ))}
      </nav>

      {tab === "map" && <OverviewTab overview={overview} />}
      {tab === "release" && <ReleasesTab />}
      {tab === "arch" && <ArchitectureTab />}
      {tab === "users" && <UsersTab />}
      {tab === "data" && <DataModelTab tenants={overview.tenants.map((t) => t.slug)} />}
      {tab === "flows" && <FlowsTab />}
      {tab === "forms" && <FormsTab />}
      {tab === "query" && <QueryTab tenants={overview.tenants.map((t) => t.slug)} />}
      {tab === "viewas" && <ViewAsTab />}
    </div>
  );
}

/* ------------------------------------------------- releases + readiness */

/**
 * The checkpoint log and a live production-readiness verdict. The checks run
 * against this deployment when the tab opens — a green board here means the
 * system actually passed, not that someone ticked a box.
 */
function ReleasesTab() {
  const [log, setLog] = useState<ReleaseLog | null>(null);
  const [ready, setReady] = useState<Readiness | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");

  function runChecks() {
    setChecking(true);
    endpoints
      .canvasReadiness()
      .then(setReady)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setChecking(false));
  }

  useEffect(() => {
    endpoints.canvasReleases().then(setLog).catch(() => setLog(null));
    runChecks();
  }, []);

  if (!log)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  const blockers = (ready?.checks ?? []).filter(
    (c) => c.severity === "blocker" && c.status === "fail",
  );
  const warns = (ready?.checks ?? []).filter((c) => c.severity === "warn" && c.status === "fail");
  const passed = (ready?.checks ?? []).filter((c) => c.status === "pass");

  return (
    <>
      <Card className={ready && !ready.ready ? "canvas-bar ready-card is-bad" : "canvas-bar ready-card is-good"}>
        <div className="arch-head">
          <div>
            <h2 className="arch-title">Production readiness</h2>
            <p className="muted">
              {ready
                ? ready.verdict
                : checking
                  ? "Running the checks against this deployment…"
                  : "Checks unavailable."}
            </p>
          </div>
          <div className="ready-verdict">
            <span className={ready?.ready ? "ready-badge is-good" : "ready-badge is-bad"}>
              {ready ? (ready.ready ? "READY" : "NOT READY") : "…"}
            </span>
            {ready && (
              <span className="muted">
                v{ready.version} · {ready.environment}
              </span>
            )}
          </div>
        </div>
        {ready && (
          <div className="chips ready-counts">
            <Badge tone="danger">{ready.blockerCount} blocker(s)</Badge>
            <Badge tone="warn">{ready.warningCount} warning(s)</Badge>
            <Badge tone="good">{ready.passCount} passing</Badge>
            <Button size="sm" variant="outline" loading={checking} onClick={runChecks}>
              Re-run checks
            </Button>
          </div>
        )}
        {error && <div className="form-err">{error}</div>}
      </Card>

      {blockers.length > 0 && (
        <Card title="Blockers" subtitle="Do not put real customer data behind these">
          <CheckList checks={blockers} />
        </Card>
      )}
      {warns.length > 0 && (
        <Card title="Warnings" subtitle="Real weaknesses that can be accepted knowingly">
          <CheckList checks={warns} />
        </Card>
      )}
      {passed.length > 0 && (
        <Card title="Passing" subtitle="Verified against this deployment just now">
          <CheckList checks={passed} />
        </Card>
      )}

      <Card
        title="Release log"
        subtitle={`Current v${log.current} · tenant schema v${log.schemaVersion}`}
      >
        <ol className="rel">
          {log.releases.map((r) => (
            <li className="rel__item" key={r.version}>
              <div className="rel__side">
                <span className="rel__v mono">v{r.version}</span>
                <span className="rel__date muted mono">{r.date}</span>
                <span className="muted rel__meta">
                  schema v{r.schema} · {r.tests} tests
                </span>
                {r.dataReset && <Badge tone="warn">data reset</Badge>}
              </div>
              <div className="rel__body">
                <h3 className="rel__title">{r.title}</h3>
                <ul className="rel__list">
                  {r.highlights.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
                {r.security.length > 0 && (
                  <>
                    <div className="rel__label">Security</div>
                    <ul className="rel__list rel__list--sec">
                      {r.security.map((h) => (
                        <li key={h}>{h}</li>
                      ))}
                    </ul>
                  </>
                )}
                {r.breaking.length > 0 && (
                  <>
                    <div className="rel__label rel__label--warn">Breaking</div>
                    <ul className="rel__list rel__list--breaking">
                      {r.breaking.map((h) => (
                        <li key={h}>{h}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      </Card>
    </>
  );
}

function CheckList({ checks }: { checks: ReadinessCheck[] }) {
  return (
    <ul className="arch">
      {checks.map((c) => (
        <li className={`arch__item is-${c.status}`} key={c.key}>
          <Badge
            tone={
              c.status === "pass" ? "good" : c.severity === "blocker" ? "danger" : "warn"
            }
          >
            {c.status === "pass" ? "Pass" : c.severity === "blocker" ? "Blocker" : "Warning"}
          </Badge>
          <div className="arch__body">
            <div className="arch__name">{c.title}</div>
            <p className="arch__detail">{c.detail}</p>
            {c.status === "fail" && c.fix && <p className="arch__note">Fix: {c.fix}</p>}
            {Object.keys(c.evidence ?? {}).length > 0 && (
              <p className="arch__note mono ready-evidence">{JSON.stringify(c.evidence)}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ----------------------------------------------------------- architecture */

function statusTone(s: string): "good" | "warn" | "neutral" {
  return s === "done" ? "good" : s === "partial" ? "warn" : "neutral";
}

/**
 * The Platform Architecture Blueprint, with each capability marked against what
 * is actually running. The point is the honesty: "planned" stays planned.
 */
function ArchitectureTab() {
  const [bp, setBp] = useState<Blueprint | null>(null);
  const [only, setOnly] = useState<string>("");

  useEffect(() => {
    endpoints.canvasArchitecture().then(setBp).catch(() => setBp(null));
  }, []);

  if (!bp)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  const keep = (s: string) => !only || s === only;
  const { counts, total, percentDone } = bp.summary;
  const pct = (k: string) => ((counts[k] ?? 0) / (total || 1)) * 100;

  return (
    <>
      <Card className="canvas-bar">
        <div className="arch-head">
          <div>
            <h2 className="arch-title">Platform Architecture Blueprint</h2>
            <p className="muted">
              The plan the platform was designed from, marked against what is running today.
              Version {bp.version}.
            </p>
          </div>
          <div className="arch-score">
            <span className="arch-score__n">{percentDone}%</span>
            <span className="muted">delivered</span>
          </div>
        </div>
        <div className="arch-bar" aria-hidden="true">
          <span className="arch-bar__done" style={{ width: `${pct("done")}%` }} />
          <span className="arch-bar__partial" style={{ width: `${pct("partial")}%` }} />
        </div>
        <div className="chips arch-filters">
          <button className={!only ? "chip is-on" : "chip"} onClick={() => setOnly("")}>
            All {total}
          </button>
          {(["done", "partial", "planned"] as const).map((s) => (
            <button
              key={s}
              className={only === s ? "chip is-on" : "chip"}
              onClick={() => setOnly(only === s ? "" : s)}
            >
              {bp.statusLabels[s]} {counts[s]}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Locked decisions" subtitle="What was chosen up front, and what happened">
        <ArchList items={bp.decisions.filter((i) => keep(i.status))} labels={bp.statusLabels} />
      </Card>

      {bp.sections.map((s) => {
        const groups = s.groups
          .map((g) => ({ ...g, items: g.items.filter((i) => keep(i.status)) }))
          .filter((g) => g.items.length > 0);
        if (groups.length === 0) return null;
        return (
          <Card key={s.key} title={`${s.num} · ${s.title}`} subtitle={s.summary}>
            {groups.map((g) => (
              <div key={g.name} className="arch-group">
                <h3 className="canvas-h3">{g.name}</h3>
                <ArchList items={g.items} labels={bp.statusLabels} />
              </div>
            ))}
          </Card>
        );
      })}
    </>
  );
}

function ArchList({
  items,
  labels,
}: {
  items: ArchItem[];
  labels: Record<string, string>;
}) {
  return (
    <ul className="arch">
      {items.map((i) => (
        <li className={`arch__item is-${i.status}`} key={i.title}>
          <Badge tone={statusTone(i.status)}>{labels[i.status]}</Badge>
          <div className="arch__body">
            <div className="arch__name">{i.title}</div>
            <p className="arch__detail">{i.detail}</p>
            {i.note && <p className="arch__note">{i.note}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ users */

/**
 * Two levels: the tenants who buy the platform, and the people inside each one.
 * A tenant's users are only ever shown under that tenant.
 */
function UsersTab() {
  const [data, setData] = useState<PlatformUsers | null>(null);
  const [open, setOpen] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [seeded, setSeeded] = useState<string>("");
  const [error, setError] = useState("");

  function load() {
    endpoints
      .canvasUsers()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }
  useEffect(load, []);

  async function seed() {
    setBusy(true);
    setError("");
    try {
      const r = await endpoints.canvasSeedDemoUsers();
      setSeeded(
        r.createdCount
          ? `Created ${r.createdCount} test account(s). Password is the role with no underscores + 12345 — e.g. projectmanager12345.`
          : `Nothing to create — all ${r.skipped} test accounts already exist.`,
      );
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (error && !data) return <Card>{error}</Card>;
  if (!data)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  return (
    <>
      <Card className="canvas-bar">
        <div className="tab-toolbar">
          <div>
            <strong>{data.tenants.length}</strong> tenants ·{" "}
            <strong>{data.totalUsers}</strong> people on the platform
          </div>
          <Button size="sm" variant="outline" loading={busy} onClick={() => void seed()}>
            Create test users for every role
          </Button>
        </div>
        {seeded && <div className="form-ok">{seeded}</div>}
        {error && <div className="form-err">{error}</div>}
      </Card>

      {data.tenants.map((t) => (
        <Card
          key={t.slug}
          title={t.name}
          subtitle={`${t.slug} · ${t.type.replace(/_/g, " ")} · ${t.userCount} user(s), ${t.activeCount} active${t.seats ? ` of ${t.seats} seats` : ""}`}
        >
          <div className="tab-toolbar">
            <div className="chips">
              <Badge tone={t.status === "active" ? "good" : "warn"}>{t.status}</Badge>
              {t.roles.map((r) => (
                <Badge key={r.key} tone="neutral">
                  {r.label}
                </Badge>
              ))}
            </div>
            <div className="chips">
              <a className="ax-btn ax-btn--outline ax-btn--sm" href={`/t/${t.slug}`}>
                Open front door
              </a>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setOpen(open === t.slug ? "" : t.slug)}
              >
                {open === t.slug ? "Hide people" : `Show ${t.userCount} people`}
              </Button>
            </div>
          </div>

          {open === t.slug &&
            (t.users.length === 0 ? (
              <p className="muted">No users in this tenant yet.</p>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl tbl--tight">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Roles</th>
                      <th>Permissions</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td className="mono u-mail">{u.email}</td>
                        <td>
                          <div className="chips">
                            {u.isPlatformAdmin && <Badge tone="danger">platform admin</Badge>}
                            {u.roles.map((r) => (
                              <Badge key={r} tone="accent">
                                {r}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="canvas-colnote mono">{u.permissions.join(" · ") || "—"}</td>
                        <td>
                          <Badge tone={u.active ? "good" : "warn"}>
                            {u.active ? "active" : "deactivated"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </Card>
      ))}
    </>
  );
}

/* ----------------------------------------------------------------- view as */

/**
 * Open the app as any user without their password. The app then behaves exactly
 * as it does for them — including losing these admin tools until you switch back.
 */
function ViewAsTab() {
  const [targets, setTargets] = useState<ViewAsTarget[] | null>(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    endpoints
      .viewAsTargets()
      .then(setTargets)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  async function start(t: ViewAsTarget) {
    setBusy(t.id);
    setError("");
    try {
      await endpoints.viewAsStart(t.id);
      // Full reload: the whole shell must re-resolve as that person.
      window.location.assign("/");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy("");
    }
  }

  if (error && !targets) return <Card>{error}</Card>;
  if (!targets)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  const q = filter.trim().toLowerCase();
  const shown = q
    ? targets.filter((t) =>
        [t.name, t.email, t.tenantName, t.tenantSlug, ...t.roles]
          .join(" ")
          .toLowerCase()
          .includes(q),
      )
    : targets;

  const byTenant = [...new Set(shown.map((t) => t.tenantSlug))];

  return (
    <>
      <Card className="canvas-bar">
        <p className="canvas-note">
          You will be signed in as the person you pick — no password needed. The app
          behaves exactly as it does for them, and these platform tools disappear until
          you switch back. Anything you do is recorded in the audit trail under their
          name <strong>and</strong> yours.
        </p>
        <div className="tab-toolbar">
          <input
            className="ax-input"
            placeholder="Search by name, email, tenant or role"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className="muted">{shown.length} account(s)</span>
        </div>
        {error && <div className="form-err">{error}</div>}
      </Card>

      {byTenant.map((slug) => (
        <Card
          key={slug}
          title={shown.find((t) => t.tenantSlug === slug)?.tenantName ?? slug}
          subtitle={slug}
          className="grid__wide"
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {shown
                  .filter((t) => t.tenantSlug === slug)
                  .map((t) => (
                    <tr key={t.id}>
                      <td>
                        <div className="u-name">{t.name}</div>
                        {!t.active && <Badge tone="warn">deactivated</Badge>}
                      </td>
                      <td className="mono u-mail">{t.email}</td>
                      <td>
                        <div className="chips">
                          {t.roles.map((r) => (
                            <Badge key={r} tone="neutral">
                              {r}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!t.active}
                          loading={busy === t.id}
                          onClick={() => void start(t)}
                        >
                          View as
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </>
  );
}

/* ---------------------------------------------------------------- overview */

function OverviewTab({ overview }: { overview: CanvasOverview }) {
  const counts = Object.entries(overview.platformCounts).sort((a, b) => b[1] - a[1]);
  return (
    <div className="grid">
      <Card title="Tenants" subtitle="One isolated database each" className="grid__wide">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Slug</th>
                <th>Name</th>
                <th>Type</th>
                <th>Users</th>
                <th>Status</th>
                <th>Modules</th>
              </tr>
            </thead>
            <tbody>
              {overview.tenants.map((t) => (
                <tr key={t.slug}>
                  <td className="mono">{t.slug}</td>
                  <td>{t.name}</td>
                  <td>{t.type.replace(/_/g, " ")}</td>
                  <td className="mono">{t.userCount}</td>
                  <td>
                    <Badge tone={t.status === "active" ? "good" : "warn"}>{t.status}</Badge>
                  </td>
                  <td>
                    <div className="chips">
                      {t.modules.map((m) => (
                        <Badge key={m} tone={m.startsWith("core.") ? "neutral" : "accent"}>
                          {m}
                        </Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Platform database" subtitle="Live row counts">
        {counts.map(([name, n]) => (
          <div className="kv" key={name}>
            <span className="kv__k mono">{name}</span>
            <span className="kv__v mono">{n.toLocaleString()}</span>
          </div>
        ))}
      </Card>

      <Card title="Platform admins" subtitle="The only role that crosses tenants">
        {overview.admins.map((a) => (
          <div className="kv" key={a.email}>
            <span className="kv__k">{a.name}</span>
            <span className="kv__v mono">{a.email}</span>
          </div>
        ))}
        <div className="kv">
          <span className="kv__k">Tenant schema version</span>
          <span className="kv__v mono">v{overview.tenantSchemaVersion}</span>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------- data model */

function DataModelTab({ tenants }: { tenants: string[] }) {
  const [source, setSource] = useState("platform");
  const [schema, setSchema] = useState<CanvasSchema | null>(null);
  const [openTable, setOpenTable] = useState<string>("");
  const [error, setError] = useState("");

  useEffect(() => {
    setSchema(null);
    setError("");
    const load =
      source === "platform"
        ? endpoints.canvasPlatformSchema()
        : endpoints.canvasTenantSchema(source.replace("tenant:", ""));
    load
      .then((s) => {
        setSchema(s);
        setOpenTable(s.tables[0]?.name ?? "");
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [source]);

  return (
    <>
      <Card className="canvas-bar">
        <div className="tab-toolbar">
          <div className="forms-filters">
            <Select value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="platform">Platform database (control plane)</option>
              {tenants.map((t) => (
                <option key={t} value={`tenant:${t}`}>
                  Tenant data DB — {t}
                </option>
              ))}
            </Select>
          </div>
          {schema && (
            <span className="muted">
              {schema.tables.length} tables ·{" "}
              {schema.tables.reduce((s, t) => s + t.columns.length, 0)} attributes
            </span>
          )}
        </div>
      </Card>

      {error && <Card>{error}</Card>}
      {!schema && !error ? (
        <div className="center-pad">
          <Spinner />
        </div>
      ) : (
        schema && (
          <div className="schema">
            <aside className="schema__list">
              {schema.tables.map((t) => (
                <button
                  key={t.name}
                  className={openTable === t.name ? "schema__item is-on" : "schema__item"}
                  onClick={() => setOpenTable(t.name)}
                >
                  <span className="mono">{t.name}</span>
                  <span className="schema__count mono">{t.rows.toLocaleString()}</span>
                </button>
              ))}
            </aside>
            <div className="schema__detail">
              {schema.tables
                .filter((t) => t.name === openTable)
                .map((t) => (
                  <TableCard key={t.name} table={t} />
                ))}
            </div>
          </div>
        )
      )}
    </>
  );
}

function TableCard({ table }: { table: CanvasTable }) {
  return (
    <Card
      title={table.name}
      subtitle={`${table.columns.length} attributes · ${table.rows.toLocaleString()} rows`}
    >
      {table.note && <p className="canvas-note">{table.note}</p>}
      {table.references.length > 0 && (
        <div className="chips canvas-refs">
          <span className="muted">Links to:</span>
          {table.references.map((r) => (
            <Badge key={r} tone="accent">
              {r}
            </Badge>
          ))}
        </div>
      )}
      <div className="tbl-wrap">
        <table className="tbl tbl--tight">
          <thead>
            <tr>
              <th>Attribute</th>
              <th>Type</th>
              <th>Rules</th>
              <th>What it is</th>
            </tr>
          </thead>
          <tbody>
            {table.columns.map((c) => (
              <tr key={c.name}>
                <td className="mono">{c.name}</td>
                <td className="mono muted">{c.type}</td>
                <td>
                  <div className="chips">
                    {c.primaryKey && <Badge tone="good">PK</Badge>}
                    {c.references && <Badge tone="accent">→ {c.references}</Badge>}
                    {!c.nullable && !c.primaryKey && <Badge tone="neutral">required</Badge>}
                    {c.index && <Badge tone="neutral">indexed</Badge>}
                    {c.default && <Badge tone="neutral">default</Badge>}
                  </div>
                </td>
                <td className="canvas-colnote">{c.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ flows */

function FlowsTab() {
  const [processes, setProcesses] = useState<CanvasProcess[] | null>(null);

  useEffect(() => {
    endpoints
      .canvasProcesses()
      .then((r) => setProcesses(r.processes))
      .catch(() => setProcesses([]));
  }, []);

  if (!processes)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  const groups = [...new Set(processes.map((p) => p.group))];

  return (
    <>
      {groups.map((g) => (
        <section key={g} className="flow-group">
          <h2 className="flow-group__title">{g}</h2>
          {processes
            .filter((p) => p.group === g)
            .map((p) => (
              <Card key={p.key} title={p.name} subtitle={p.summary} className="flow-card">
                <ol className="flow">
                  {p.steps.map((s, i) => (
                    <li className="flow__step" key={`${p.key}-${i}`}>
                      <span className="flow__n mono">{i + 1}</span>
                      <div className="flow__body">
                        <div className="flow__top">
                          <Badge tone="accent">{s.actor}</Badge>
                          <strong>{s.title}</strong>
                        </div>
                        {s.detail && <p className="flow__detail">{s.detail}</p>}
                        <div className="flow__meta">
                          {s.endpoint && <code className="flow__endpoint">{s.endpoint}</code>}
                          {s.tables.map((t) => (
                            <Badge key={t} tone="neutral">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </Card>
            ))}
        </section>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ forms */

function FormsTab() {
  const [forms, setForms] = useState<FormType[] | null>(null);
  const [open, setOpen] = useState("");

  useEffect(() => {
    endpoints
      .canvasForms()
      .then((r) => {
        setForms(r.forms);
        setOpen(r.forms[0]?.code ?? "");
      })
      .catch(() => setForms([]));
  }, []);

  if (!forms)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  return (
    <div className="schema">
      <aside className="schema__list">
        {forms.map((f) => (
          <button
            key={f.code}
            className={open === f.code ? "schema__item is-on" : "schema__item"}
            onClick={() => setOpen(f.code)}
          >
            <span className="mono">{f.code}</span>
            <span className="schema__count">{f.title}</span>
          </button>
        ))}
      </aside>
      <div className="schema__detail">
        {forms
          .filter((f) => f.code === open)
          .map((f) => (
            <Card
              key={f.code}
              title={`${f.code} — ${f.title}`}
              subtitle={f.summary}
              className="grid__wide"
            >
              <div className="form-meta">
                <span>
                  Raised by: <strong>{f.originator.join(", ")}</strong>
                </span>
                <span>
                  Files in: <strong>{f.folder}</strong>
                </span>
                <span>
                  Reply within: <strong>{f.slaDays} days</strong>
                </span>
                {f.urgent && <Badge tone="danger">urgent</Badge>}
                {f.spawns.length > 0 && <span>Can lead to: {f.spawns.join(", ")}</span>}
              </div>

              <h3 className="canvas-h3">Items to be filled</h3>
              <div className="tbl-wrap">
                <table className="tbl tbl--tight">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Type</th>
                      <th>Required</th>
                      <th>Options</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.fields.map((fl) => (
                      <tr key={fl.key}>
                        <td>
                          {fl.label}
                          <div className="u-mail mono">{fl.key}</div>
                        </td>
                        <td className="mono muted">{fl.type}</td>
                        <td>{fl.required ? <Badge tone="warn">required</Badge> : "—"}</td>
                        <td>
                          <div className="chips">
                            {(fl.options ?? []).map((o) => (
                              <Badge key={o} tone="neutral">
                                {o}
                              </Badge>
                            ))}
                          </div>
                          {fl.hint && <div className="u-mail">{fl.hint}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="canvas-h3">Route and decisions</h3>
              <ol className="flow">
                {f.route.map((s, i) => (
                  <li className="flow__step" key={s.key}>
                    <span className="flow__n mono">{i + 1}</span>
                    <div className="flow__body">
                      <div className="flow__top">
                        <Badge tone="accent">{s.actorLabel}</Badge>
                        <strong>{s.label}</strong>
                      </div>
                      <div className="flow__meta">
                        {s.decisions.map((d) => (
                          <Badge
                            key={d.key}
                            tone={
                              d.tone === "good"
                                ? "good"
                                : d.tone === "bad"
                                  ? "danger"
                                  : d.tone === "warn"
                                    ? "warn"
                                    : "neutral"
                            }
                          >
                            {d.label} →{" "}
                            {d.next === "__close__"
                              ? "closed"
                              : d.next === "__originator__"
                                ? "back to originator"
                                : d.next}
                          </Badge>
                        ))}
                      </div>
                      {s.fields.length > 0 && (
                        <p className="flow__detail">
                          Also fills: {s.fields.map((x) => x.label).join(", ")}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ query */

const SAMPLES: { label: string; source: string; sql: string }[] = [
  {
    label: "Every user and their tenant",
    source: "platform",
    sql: "SELECT email, name, tenant_slug, roles, active, is_platform_admin FROM users",
  },
  {
    label: "Each person's saved settings",
    source: "platform",
    sql: "SELECT u.email, s.mode, s.locale, s.font, s.notify\nFROM user_settings s JOIN users u ON u.id = s.user_id",
  },
  {
    label: "Tenants and their modules",
    source: "platform",
    sql: "SELECT slug, name, type, status, modules FROM tenants",
  },
];

function QueryTab({ tenants }: { tenants: string[] }) {
  const [source, setSource] = useState("platform");
  const [sql, setSql] = useState(SAMPLES[0]?.sql ?? "SELECT * FROM users");
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const tenantSamples = useMemo(
    () =>
      tenants.length
        ? [
            {
              label: "Forms and where they sit",
              source: `tenant:${tenants[0]}`,
              sql: "SELECT number, subject, status, current_step, originator_name, rev\nFROM form_instances ORDER BY created_at DESC",
            },
            {
              label: "The audit trail",
              source: `tenant:${tenants[0]}`,
              sql: "SELECT f.number, a.seq, a.actor_name, a.actor_party, a.decision_label, a.internal, a.created_at\nFROM form_actions a JOIN form_instances f ON f.id = a.form_id\nORDER BY f.number, a.seq",
            },
            {
              label: "Project teams and abilities",
              source: `tenant:${tenants[0]}`,
              sql: "SELECT p.name AS project, m.name, m.email, m.role, m.group_name, m.abilities\nFROM project_members m JOIN projects p ON p.id = m.project_id",
            },
          ]
        : [],
    [tenants],
  );

  async function run() {
    setBusy(true);
    setError("");
    try {
      setResult(await endpoints.canvasQuery(source, sql));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card className="canvas-bar">
        <div className="tab-toolbar">
          <div className="forms-filters">
            <Select value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="platform">Platform database</option>
              {tenants.map((t) => (
                <option key={t} value={`tenant:${t}`}>
                  Tenant data DB — {t}
                </option>
              ))}
            </Select>
          </div>
          <span className="muted">Read-only — SELECT and WITH only, max 200 rows</span>
        </div>
        <div className="chips canvas-samples">
          {[...SAMPLES, ...tenantSamples].map((s) => (
            <button
              key={s.label}
              className="chip"
              onClick={() => {
                setSource(s.source);
                setSql(s.sql);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        <textarea
          className="ax-input ax-textarea canvas-sql mono"
          rows={5}
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          spellCheck={false}
        />
        <div className="brand-actions">
          <Button loading={busy} onClick={() => void run()}>
            Run
          </Button>
        </div>
        {error && <div className="form-err">{error}</div>}
      </Card>

      {result && (
        <Card
          title="Result"
          subtitle={`${result.rowCount} row(s)${result.truncated ? " — truncated at 200" : ""}`}
        >
          {result.rowCount === 0 ? (
            <p className="muted">No rows.</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl tbl--tight">
                <thead>
                  <tr>
                    {result.columns.map((c) => (
                      <th key={c} className="mono">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} className="mono canvas-cell">
                          {cell === null ? <span className="muted">null</span> : String(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </>
  );
}
